import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

const $ = window.$;

// React wrapper for jQuery Date Range Picker component
function DateRange({
  filters,
  setFilters,
  controlledDateRange,
  setControlledDateRange,
  isControlled = false,
  datePickerMode = 'daterange',
}) {
  const el = useRef(null);

  const momentStartDate = moment(filters.start_date, 'YYYYMMDD');
  const momentEndDate = moment(filters.end_date, 'YYYYMMDD');

  // isControlled is true when the DateRange component is rendered within the ReportFilters sidebar.
  // If isControlled is true we want to keep track of the selected date in the controlledDateRange state
  // without setting the date in the filters state until the user clicks apply. If isControlled is false,
  // the DateRange component is being rendered in the body of the page and it should update the date in
  // the filters state whenever it is changed
  let label;
  if (isControlled) {
    if (datePickerMode === 'singledate') {
      label = `${controlledDateRange.end.format('MMMM, YYYY')}`;
    } else {
      label = `${controlledDateRange.start.format(
        'MMMM D, YYYY'
      )} - ${controlledDateRange.end.format('MMMM D, YYYY')}`;
    }
  } else {
    if (datePickerMode === 'singledate') {
      label = `${momentEndDate.format('MMMM, YYYY')}`;
    } else {
      label = `${momentStartDate.format('MMMM D, YYYY')} - ${momentEndDate.format('MMMM D, YYYY')}`;
    }
  }

  useEffect(() => {
    const $el = $(el.current);
    $el.daterangepicker(
      {
        format: 'MM/DD/YYYY',
        startDate: momentStartDate,
        endDate: momentEndDate,
        ...(isControlled && { startDate: controlledDateRange.start }),
        ...(isControlled && { endDate: controlledDateRange.end }),
        opens: 'left',
        maxDate:
          datePickerMode === 'singledate'
            ? // Performance summary report max date is the previous month, and is currently the only
              // report that uses the singledate mode. If we add more reports that use singledate mode
              // we should make the maxDate configurable.
              moment().subtract(1, 'months').endOf('month')
            : moment(),
        showDropdowns: datePickerMode === 'singledate' ? true : false,
        showWeekNumbers: false,
        timePicker: false,
        ranges: {
          Today: [moment().startOf('day'), moment().startOf('day')],
          Yesterday: [
            moment().subtract(1, 'days').startOf('day'),
            moment().subtract(1, 'days').startOf('day'),
          ],
          'This Month': [moment().startOf('month'), moment().endOf('month')],
          'Last Month': [
            moment().subtract(1, 'month').startOf('month'),
            moment().subtract(1, 'month').endOf('month'),
          ],
        },
        buttonClasses: ['btn btn-sm'],
        applyClass: 'btn-primary',
        cancelClass: 'btn-default',
        separator: ' to ',
        locale: {
          applyLabel: 'Apply',
          fromLabel: 'From',
          toLabel: 'To',
          customRangeLabel: 'Custom Range',
          daysOfWeek: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
          monthNames: [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
          ],
          firstDay: 0,
        },
        singleDatePicker: datePickerMode === 'singledate' ? true : false,
      },
      (start, end) => {
        if (isControlled) {
          // Just updates UI
          setControlledDateRange({ start, end });
        } else {
          // Updates UI and sets filter state
          setControlledDateRange({ start, end });
          setFilters((prevFilters) => ({
            ...prevFilters,
            start_date: start.format('YYYYMMDD'),
            end_date: end.format('YYYYMMDD'),
          }));
        }
      }
    );
  }, [
    controlledDateRange,
    setFilters,
    isControlled,
    setControlledDateRange,
    momentStartDate,
    momentEndDate,
    datePickerMode,
  ]);

  return (
    <div
      ref={el}
      id='reportrange'
      style={{ cursor: 'pointer', display: 'inline', fontSize: '14px' }}
    >
      <i className='fa fa-calendar' /> <span>{label}</span> <i className='fa fa-caret-down' />
    </div>
  );
}

DateRange.propTypes = {
  controlledDateRange: PropTypes.shape({
    start: PropTypes.object,
    end: PropTypes.object,
  }).isRequired,
  setControlledDateRange: PropTypes.func.isRequired,
  isControlled: PropTypes.bool,
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  datePickerMode: PropTypes.string,
};

export default DateRange;
